// src/features/test-cases/components/TestCaseDialog.tsx
// Create/Edit test case dialog with suite membership management
// OpenAPI: POST /api/testcases (testcase-service), suite membership via hierarchy-service

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useCreateTestCase, useUpdateTestCase } from '../api/useTestCases'
import { useTestSuites } from '@/features/test-suites/api/useTestSuites'
import { useAuthStore } from '@/lib/auth/authStore'
import { apiClient } from '@/lib/api/client'
import type { TestCase } from '@/types/testcase.types'
import { TestImportance, ExecutionType } from '@/types/testcase.types'
import { logger } from '@/lib/utils/logger'

interface TestCaseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  testCase?: TestCase
  mode: 'create' | 'edit'
}

export function TestCaseDialog({
  open,
  onOpenChange,
  testCase,
  mode,
}: TestCaseDialogProps) {
  const user = useAuthStore((state) => state.user)
  const [name, setName] = useState('')
  const [selectedSuiteIds, setSelectedSuiteIds] = useState<number[]>([])
  const [summary, setSummary] = useState('')
  const [preconditions, setPreconditions] = useState('')
  const [importance, setImportance] = useState<string>(
    TestImportance.Medium.toString()
  )
  const [executionType, setExecutionType] = useState<string>(
    ExecutionType.Manual.toString()
  )
  const [estimatedDuration, setEstimatedDuration] = useState('')
  const [error, setError] = useState<string | null>(null)

  const createMutation = useCreateTestCase()
  const updateMutation = useUpdateTestCase()
  const { data: testSuites, isLoading: isLoadingSuites } = useTestSuites()

  useEffect(() => {
    if (mode === 'edit' && testCase && open) {
      setName(testCase.name)
      setSummary(testCase.summary || '')
      setPreconditions(testCase.preconditions || '')
      setImportance(testCase.importance.toString())
      setExecutionType(testCase.execution_type.toString())
      setEstimatedDuration(testCase.estimated_exec_duration?.toString() || '')

      apiClient
        .get(`/api/hierarchy/testcases/${testCase.id}/memberships`)
        .then((response) => {
          const suiteIds = response.data.suites.map((s: { suite_id: number }) => s.suite_id)
          setSelectedSuiteIds(suiteIds)
        })
        .catch((err) => {
          logger.error('Failed to fetch memberships', { error: err })
          setSelectedSuiteIds([])
        })
    } else if (mode === 'create') {
      setName('')
      setSelectedSuiteIds([])
      setSummary('')
      setPreconditions('')
      setImportance(TestImportance.Medium.toString())
      setExecutionType(ExecutionType.Manual.toString())
      setEstimatedDuration('')
    }
    setError(null)
  }, [mode, testCase, open])

  const toggleSuiteSelection = (suiteId: number) => {
    setSelectedSuiteIds((prev) =>
      prev.includes(suiteId)
        ? prev.filter((id) => id !== suiteId)
        : [...prev, suiteId]
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isLoading) return

    setError(null)

    if (!user?.id) {
      setError('User not authenticated')
      return
    }

    if (!name.trim()) {
      setError('Test case name is required')
      return
    }

    const authorId = parseInt(String(user?.id), 10)
    if (isNaN(authorId)) {
      setError('Invalid user ID')
      return
    }

    const duration = estimatedDuration.trim()
      ? parseInt(estimatedDuration, 10)
      : undefined

    try {
      if (mode === 'create') {
        // POST /api/testcases — body per openapi testcase_service_CreateTestCaseRequest
        const createdTestCase = await createMutation.mutateAsync({
          name: name.trim(),
          author_id: authorId,
          summary: summary.trim() || undefined,
          preconditions: preconditions.trim() || undefined,
          importance: parseInt(importance, 10),
          execution_type: parseInt(executionType, 10),
          estimated_exec_duration: duration,
        })

        // Add to selected suites (many-to-many via hierarchy-service)
        if (selectedSuiteIds.length > 0) {
          for (const suiteId of selectedSuiteIds) {
            try {
              await apiClient.post(
                `/api/suites/${suiteId}/testcases`,
                { testcase_id: createdTestCase.id, node_order: 1 }
              )
            } catch (err) {
              logger.error(`Failed to add test case to suite ${suiteId}`, { error: err })
            }
          }
        }
      } else if (testCase) {
        // PUT /api/testcases/{testcase_id} — body per openapi testcase_service_UpdateTestCaseRequest
        await updateMutation.mutateAsync({
          id: testCase.id,
          data: {
            name: name.trim(),
            summary: summary.trim() || undefined,
            preconditions: preconditions.trim() || undefined,
            importance: parseInt(importance, 10),
            execution_type: parseInt(executionType, 10),
            estimated_exec_duration: duration,
          },
          updaterId: authorId,
        })

        // Sync suite memberships
        const currentMemberships = await apiClient.get(
          `/api/hierarchy/testcases/${testCase.id}/memberships`
        )
        const currentSuiteIds: number[] = currentMemberships.data.suites.map(
          (s: { suite_id: number }) => s.suite_id
        )

        const suitesToAdd = selectedSuiteIds.filter(
          (id) => !currentSuiteIds.includes(id)
        )
        for (const suiteId of suitesToAdd) {
          try {
            await apiClient.post(
              `/api/suites/${suiteId}/testcases`,
              { testcase_id: testCase.id, node_order: 1 }
            )
          } catch (err) {
            logger.error(`Failed to add test case to suite ${suiteId}`, { error: err })
          }
        }

        const suitesToRemove = currentSuiteIds.filter(
          (id: number) => !selectedSuiteIds.includes(id)
        )
        for (const suiteId of suitesToRemove) {
          try {
            await apiClient.delete(
              `/api/suites/${suiteId}/testcases/${testCase.id}`
            )
          } catch (err) {
            logger.error(`Failed to remove test case from suite ${suiteId}`, { error: err })
          }
        }
      }
      onOpenChange(false)
    } catch (err: unknown) {
      let errorMessage = 'Failed to save test case'
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { detail?: string } } }
        if (axiosError.response?.data?.detail) {
          errorMessage = axiosError.response.data.detail
        }
      } else if (err instanceof Error) {
        errorMessage = err.message
      }
      setError(errorMessage)
    }
  }

  const isLoading = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create New Test Case' : 'Edit Test Case'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Add a new test case and assign it to one or more test suites.'
              : 'Update test case information and suite memberships.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid gap-2">
              <Label htmlFor="name">
                Test Case Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Verify user login functionality"
                disabled={isLoading}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label>
                Test Suites (select one or more)
                {mode === 'create' && <span className="text-red-500"> *</span>}
              </Label>
              <div className="border rounded-md p-3 max-h-48 overflow-y-auto">
                {isLoadingSuites && (
                  <p className="text-sm text-gray-500">Loading suites...</p>
                )}
                {!isLoadingSuites && testSuites && testSuites.length === 0 && (
                  <p className="text-sm text-gray-500">No test suites available</p>
                )}
                {testSuites?.map((suite) => (
                  <div key={suite.id} className="flex items-center space-x-2 py-2">
                    <Checkbox
                      id={`suite-${suite.id}`}
                      checked={selectedSuiteIds.includes(suite.id)}
                      onCheckedChange={() => toggleSuiteSelection(suite.id)}
                      disabled={isLoading}
                    />
                    <label
                      htmlFor={`suite-${suite.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {suite.name}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                {mode === 'create'
                  ? 'Select at least one suite for this test case'
                  : 'Update suite memberships (test case can belong to multiple suites)'}
              </p>
              {mode === 'create' && selectedSuiteIds.length === 0 && (
                <p className="text-xs text-red-500">Please select at least one suite</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="summary">Summary</Label>
              <Textarea
                id="summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="Brief summary of the test case"
                disabled={isLoading}
                rows={3}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="preconditions">Preconditions</Label>
              <Textarea
                id="preconditions"
                value={preconditions}
                onChange={(e) => setPreconditions(e.target.value)}
                placeholder="Prerequisites or setup required"
                disabled={isLoading}
                rows={3}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="estimatedDuration">Estimated Duration (minutes)</Label>
              <Input
                id="estimatedDuration"
                type="number"
                min="0"
                value={estimatedDuration}
                onChange={(e) => setEstimatedDuration(e.target.value)}
                placeholder="e.g., 15"
                disabled={isLoading}
              />
              <p className="text-xs text-gray-500">Estimated time to execute this test case</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="importance">Importance</Label>
                <Select value={importance} onValueChange={setImportance} disabled={isLoading}>
                  <SelectTrigger id="importance">
                    <SelectValue placeholder="Select importance" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TestImportance.Low.toString()}>Low</SelectItem>
                    <SelectItem value={TestImportance.Medium.toString()}>Medium</SelectItem>
                    <SelectItem value={TestImportance.High.toString()}>High</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="executionType">Execution Type</Label>
                <Select value={executionType} onValueChange={setExecutionType} disabled={isLoading}>
                  <SelectTrigger id="executionType">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ExecutionType.Manual.toString()}>Manual</SelectItem>
                    <SelectItem value={ExecutionType.Automated.toString()}>Automated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#1976D2] hover:bg-[#1565C0]"
              disabled={isLoading || (mode === 'create' && selectedSuiteIds.length === 0)}
            >
              {isLoading
                ? mode === 'create'
                  ? 'Creating...'
                  : 'Updating...'
                : mode === 'create'
                  ? 'Create Test Case'
                  : 'Update Test Case'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
